/**
 * Athena billing HTTP client for live `/billing/v1/*`, `/admin/billing/*`,
 * provider webhook, and `/debug/billing` routes.
 *
 * Methods map 1:1 onto the registered Actix handlers. Auth uses the static
 * Athena admin key (`X-Athena-Key` / configured API key) matching the server
 * `authorize_static_admin_key` gate.
 *
 * Contract spine: `billingSdkManifest` METHOD+path entries must match the
 * permanent Rust inventory `athena_billing::LIVE_HTTP_ROUTES`
 * (exported as `./live-http-routes.json`). Export with
 * `cargo run -p athena-billing --bin billing-contract-spine -- --write`.
 */

import { buildSdkHeaderValue } from '../sdk-version.ts'
import {
  buildAthenaRequestHeaders,
  hasHeaderIgnoreCase,
} from '../utils/athena-request-headers.ts'
import { normalizeAthenaGatewayBaseUrl } from '../gateway/url.ts'

const SDK_NAME = 'xylex-group/athena-billing'
const SDK_HEADER_VALUE = buildSdkHeaderValue(SDK_NAME)

export type AthenaBillingHttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

export type AthenaBillingJson =
  | null
  | boolean
  | number
  | string
  | AthenaBillingJson[]
  | { [key: string]: AthenaBillingJson }

export interface AthenaBillingCallOptions {
  headers?: Record<string, string>
  signal?: AbortSignal
  apiKey?: string | null
}

export interface AthenaBillingClientConfig {
  baseUrl: string
  apiKey: string
  client?: string | null
  headers?: Record<string, string>
  fetchImpl?: typeof fetch
}

export interface AthenaBillingEnvelope<T = unknown> {
  status?: string
  message?: string
  data: T
}

export class AthenaBillingError extends Error {
  status: number
  endpoint: string
  method: string
  body: unknown

  constructor(input: {
    message: string
    status: number
    endpoint: string
    method: string
    body: unknown
  }) {
    super(input.message)
    this.name = 'AthenaBillingError'
    this.status = input.status
    this.endpoint = input.endpoint
    this.method = input.method
    this.body = input.body
  }
}

export interface BillingConnectionRefInput {
  connectionId: string
  clientName?: string
}

export interface BillingListQuery extends BillingConnectionRefInput {
  cursor?: string
  source?: 'provider' | 'persistence' | string
  limit?: number
  offset?: number
}

export interface BillingEnsureCustomerInput extends BillingConnectionRefInput {
  email?: string | null
  name?: string | null
  metadata?: Record<string, AthenaBillingJson>
  idempotencyKey: string
}

export interface BillingUpdateCustomerInput extends BillingConnectionRefInput {
  email?: string | null
  name?: string | null
}

export interface BillingCreateConnectionInput {
  ownerKind: string
  ownerId: string
  provider: string
  mode?: string
  status?: string
  credentialKind?: string
  providerAccountId: string
  providerProfileId?: string | null
  scopes?: string[]
  config?: Record<string, AthenaBillingJson>
  metadata?: Record<string, AthenaBillingJson>
}

export interface BillingUpdateConnectionInput {
  status?: string
  mode?: string
  credentialKind?: string
  providerAccountId?: string
  providerProfileId?: string | null
  scopes?: string[]
  config?: Record<string, AthenaBillingJson>
  metadata?: Record<string, AthenaBillingJson>
}

export interface BillingReconcileInput {
  resourceKind: string
  resourceId: string
  customerId?: string | null
}

export interface BillingProvisionSinksInput {
  targetSchema?: string
  instance?: string
}

export const billingSdkManifest = {
  namespace: 'billing',
  envelopeKind: 'athena',
  methods: [
    { name: 'getCapabilities', method: 'GET', path: '/billing/v1/capabilities' },
    { name: 'createCheckout', method: 'POST', path: '/billing/v1/checkouts' },
    { name: 'listProducts', method: 'GET', path: '/billing/v1/products' },
    { name: 'listPrices', method: 'GET', path: '/billing/v1/prices' },
    { name: 'listCustomers', method: 'GET', path: '/billing/v1/customers' },
    { name: 'createCustomer', method: 'POST', path: '/billing/v1/customers' },
    { name: 'getCustomer', method: 'GET', path: '/billing/v1/customers/{id}' },
    { name: 'updateCustomer', method: 'PATCH', path: '/billing/v1/customers/{id}' },
    { name: 'deleteCustomer', method: 'DELETE', path: '/billing/v1/customers/{id}' },
    { name: 'listPayments', method: 'GET', path: '/billing/v1/payments' },
    { name: 'createPayment', method: 'POST', path: '/billing/v1/payments' },
    { name: 'getPayment', method: 'GET', path: '/billing/v1/payments/{id}' },
    { name: 'cancelPayment', method: 'POST', path: '/billing/v1/payments/{id}/cancel' },
    { name: 'listPaymentLinks', method: 'GET', path: '/billing/v1/payment-links' },
    { name: 'createPaymentLink', method: 'POST', path: '/billing/v1/payment-links' },
    { name: 'getPaymentLink', method: 'GET', path: '/billing/v1/payment-links/{id}' },
    { name: 'updatePaymentLink', method: 'PATCH', path: '/billing/v1/payment-links/{id}' },
    { name: 'deletePaymentLink', method: 'DELETE', path: '/billing/v1/payment-links/{id}' },
    { name: 'listRefunds', method: 'GET', path: '/billing/v1/refunds' },
    { name: 'createRefund', method: 'POST', path: '/billing/v1/refunds' },
    { name: 'getRefund', method: 'GET', path: '/billing/v1/refunds/{id}' },
    { name: 'cancelRefund', method: 'POST', path: '/billing/v1/refunds/{id}/cancel' },
    { name: 'listSubscriptions', method: 'GET', path: '/billing/v1/subscriptions' },
    { name: 'createSubscription', method: 'POST', path: '/billing/v1/subscriptions' },
    { name: 'getSubscription', method: 'GET', path: '/billing/v1/subscriptions/{id}' },
    { name: 'updateSubscription', method: 'PATCH', path: '/billing/v1/subscriptions/{id}' },
    { name: 'cancelSubscription', method: 'POST', path: '/billing/v1/subscriptions/{id}/cancel' },
    { name: 'listInvoices', method: 'GET', path: '/billing/v1/invoices' },
    { name: 'getInvoice', method: 'GET', path: '/billing/v1/invoices/{id}' },
    { name: 'listWebhooks', method: 'GET', path: '/billing/v1/webhooks' },
    { name: 'createWebhook', method: 'POST', path: '/billing/v1/webhooks' },
    { name: 'getWebhook', method: 'GET', path: '/billing/v1/webhooks/{id}' },
    { name: 'updateWebhook', method: 'PATCH', path: '/billing/v1/webhooks/{id}' },
    { name: 'deleteWebhook', method: 'DELETE', path: '/billing/v1/webhooks/{id}' },
    { name: 'testWebhook', method: 'POST', path: '/billing/v1/webhooks/{id}/test' },
    {
      name: 'listConnections',
      method: 'GET',
      path: '/admin/billing/clients/{client_name}/connections',
    },
    {
      name: 'createConnection',
      method: 'POST',
      path: '/admin/billing/clients/{client_name}/connections',
    },
    {
      name: 'getConnection',
      method: 'GET',
      path: '/admin/billing/clients/{client_name}/connections/{connection_id}',
    },
    {
      name: 'updateConnection',
      method: 'PATCH',
      path: '/admin/billing/clients/{client_name}/connections/{connection_id}',
    },
    {
      name: 'deleteConnection',
      method: 'DELETE',
      path: '/admin/billing/clients/{client_name}/connections/{connection_id}',
    },
    {
      name: 'reconcileDocument',
      method: 'POST',
      path: '/admin/billing/clients/{client_name}/connections/{connection_id}/reconcile',
    },
    {
      name: 'listWebhookEvents',
      method: 'GET',
      path: '/admin/billing/clients/{client_name}/webhook-events',
    },
    {
      name: 'provisionWebhookSinks',
      method: 'POST',
      path: '/admin/billing/clients/{client_name}/webhook-sinks/provision',
    },
    { name: 'listGrants', method: 'GET', path: '/admin/billing/grants' },
    { name: 'listProviders', method: 'GET', path: '/admin/billing/providers' },
    {
      name: 'listSinkHelpers',
      method: 'GET',
      path: '/admin/webhook-sinks/helpers/billing',
    },
    {
      name: 'ingestProviderWebhook',
      method: 'POST',
      path: '/billing/providers/{provider}/clients/{client_name}/connections/{connection_id}/webhook',
    },
    { name: 'getDebugBilling', method: 'GET', path: '/debug/billing' },
  ],
} as const

function withPathParam(path: string, name: string, value: string): string {
  return path.replace(`{${name}}`, encodeURIComponent(value))
}

function appendQuery(
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const serialized = params.toString()
  return serialized ? `${path}?${serialized}` : path
}

function connectionQuery(input: BillingListQuery | BillingConnectionRefInput): Record<string, string> {
  const query: Record<string, string> = {
    connectionId: input.connectionId,
  }
  if (input.clientName) query.clientName = input.clientName
  if ('cursor' in input && input.cursor) query.cursor = input.cursor
  if ('source' in input && input.source) query.source = input.source
  if ('limit' in input && input.limit !== undefined) query.limit = String(input.limit)
  if ('offset' in input && input.offset !== undefined) query.offset = String(input.offset)
  return query
}

function parseEnvelopeData<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as AthenaBillingEnvelope<T>).data
  }
  return body as T
}

export interface AthenaBillingModule {
  getCapabilities(input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  createCheckout(body: Record<string, unknown>, options?: AthenaBillingCallOptions): Promise<unknown>
  listProducts(input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listPrices(
    input: BillingConnectionRefInput & { productId?: string },
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  listCustomers(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  createCustomer(input: BillingEnsureCustomerInput, options?: AthenaBillingCallOptions): Promise<unknown>
  getCustomer(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  updateCustomer(
    id: string,
    input: BillingUpdateCustomerInput,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  deleteCustomer(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listPayments(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  createPayment(body: Record<string, unknown>, options?: AthenaBillingCallOptions): Promise<unknown>
  getPayment(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  cancelPayment(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listPaymentLinks(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  createPaymentLink(body: Record<string, unknown>, options?: AthenaBillingCallOptions): Promise<unknown>
  getPaymentLink(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  updatePaymentLink(
    id: string,
    body: Record<string, unknown>,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  deletePaymentLink(
    id: string,
    input: BillingConnectionRefInput,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  listRefunds(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  createRefund(body: Record<string, unknown>, options?: AthenaBillingCallOptions): Promise<unknown>
  getRefund(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  cancelRefund(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listSubscriptions(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  createSubscription(body: Record<string, unknown>, options?: AthenaBillingCallOptions): Promise<unknown>
  getSubscription(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  updateSubscription(
    id: string,
    body: Record<string, unknown>,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  cancelSubscription(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listInvoices(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  getInvoice(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listWebhooks(input: BillingListQuery, options?: AthenaBillingCallOptions): Promise<unknown>
  createWebhook(
    input: BillingConnectionRefInput,
    body: Record<string, unknown>,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  getWebhook(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  updateWebhook(
    id: string,
    input: BillingConnectionRefInput,
    body: Record<string, unknown>,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  deleteWebhook(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  testWebhook(id: string, input: BillingConnectionRefInput, options?: AthenaBillingCallOptions): Promise<unknown>
  listConnections(clientName: string, options?: AthenaBillingCallOptions): Promise<unknown>
  createConnection(
    clientName: string,
    input: BillingCreateConnectionInput,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  getConnection(
    clientName: string,
    connectionId: string,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  updateConnection(
    clientName: string,
    connectionId: string,
    input: BillingUpdateConnectionInput,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  deleteConnection(
    clientName: string,
    connectionId: string,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  reconcileDocument(
    clientName: string,
    connectionId: string,
    input: BillingReconcileInput,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  listWebhookEvents(clientName: string, options?: AthenaBillingCallOptions): Promise<unknown>
  provisionWebhookSinks(
    clientName: string,
    input?: BillingProvisionSinksInput,
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  listGrants(options?: AthenaBillingCallOptions): Promise<unknown>
  listProviders(options?: AthenaBillingCallOptions): Promise<unknown>
  listSinkHelpers(
    query?: { targetSchema?: string; instance?: string },
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  ingestProviderWebhook(
    input: {
      provider: string
      clientName: string
      connectionId: string
      body: BodyInit | Record<string, unknown> | string
      signatureHeaders?: Record<string, string>
    },
    options?: AthenaBillingCallOptions,
  ): Promise<unknown>
  getDebugBilling(jwtSecret: string, options?: AthenaBillingCallOptions): Promise<string>
}

export function createBillingModule(config: AthenaBillingClientConfig): AthenaBillingModule {
  const baseUrl = normalizeAthenaGatewayBaseUrl(config.baseUrl, {
    label: 'Athena billing base URL',
  })
  const fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis)

  async function call<T>(
    method: AthenaBillingHttpMethod,
    path: string,
    body?: unknown,
    options?: AthenaBillingCallOptions,
    responseType: 'json' | 'text' = 'json',
  ): Promise<T> {
    const headers = buildAthenaRequestHeaders({
      profile: 'gateway',
      sdkHeaderValue: SDK_HEADER_VALUE,
      apiKey: options?.apiKey ?? config.apiKey,
      client: config.client,
      configHeaders: {
        ...(config.headers ?? {}),
      },
      callHeaders: options?.headers,
      contentType: 'application/json',
    })

    let requestBody: BodyInit | undefined
    if (body !== undefined && body !== null) {
      if (
        typeof body === 'string' ||
        body instanceof Blob ||
        body instanceof ArrayBuffer ||
        ArrayBuffer.isView(body) ||
        body instanceof FormData ||
        body instanceof URLSearchParams
      ) {
        requestBody = body as BodyInit
      } else {
        requestBody = JSON.stringify(body)
        if (!hasHeaderIgnoreCase(headers, 'Content-Type')) {
          headers['Content-Type'] = 'application/json'
        }
      }
    }

    const targetUrl = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`
    const response = await fetchImpl(targetUrl, {
      method,
      headers,
      body: requestBody,
      signal: options?.signal,
    })

    if (responseType === 'text') {
      const text = await response.text()
      if (!response.ok) {
        throw new AthenaBillingError({
          message: `Billing request failed (${response.status})`,
          status: response.status,
          endpoint: path,
          method,
          body: text,
        })
      }
      return text as T
    }

    const rawText = await response.text()
    let parsed: unknown = rawText
    if (rawText) {
      try {
        parsed = JSON.parse(rawText)
      } catch {
        parsed = rawText
      }
    } else {
      parsed = null
    }

    if (!response.ok) {
      const message =
        parsed &&
        typeof parsed === 'object' &&
        'message' in parsed &&
        typeof (parsed as { message: unknown }).message === 'string'
          ? (parsed as { message: string }).message
          : `Billing request failed (${response.status})`
      throw new AthenaBillingError({
        message,
        status: response.status,
        endpoint: path,
        method,
        body: parsed,
      })
    }

    return parseEnvelopeData<T>(parsed)
  }

  return {
    getCapabilities(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/capabilities', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createCheckout(body, options) {
      return call('POST', '/billing/v1/checkouts', body, options)
    },
    listProducts(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/products', connectionQuery(input)),
        undefined,
        options,
      )
    },
    listPrices(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/prices', {
          ...connectionQuery(input),
          ...(input.productId ? { productId: input.productId } : {}),
        }),
        undefined,
        options,
      )
    },
    listCustomers(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/customers', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createCustomer(input, options) {
      return call(
        'POST',
        '/billing/v1/customers',
        {
          connectionId: input.connectionId,
          clientName: input.clientName,
          email: input.email,
          name: input.name,
          metadata: input.metadata ?? {},
          idempotencyKey: input.idempotencyKey,
        },
        options,
      )
    },
    getCustomer(id, input, options) {
      return call(
        'GET',
        appendQuery(
          withPathParam('/billing/v1/customers/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    updateCustomer(id, input, options) {
      return call(
        'PATCH',
        withPathParam('/billing/v1/customers/{id}', 'id', id),
        {
          connectionId: input.connectionId,
          clientName: input.clientName,
          email: input.email,
          name: input.name,
        },
        options,
      )
    },
    deleteCustomer(id, input, options) {
      return call(
        'DELETE',
        appendQuery(
          withPathParam('/billing/v1/customers/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    listPayments(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/payments', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createPayment(body, options) {
      return call('POST', '/billing/v1/payments', body, options)
    },
    getPayment(id, input, options) {
      return call(
        'GET',
        appendQuery(
          withPathParam('/billing/v1/payments/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    cancelPayment(id, input, options) {
      return call(
        'POST',
        appendQuery(
          withPathParam('/billing/v1/payments/{id}/cancel', 'id', id),
          connectionQuery(input),
        ),
        {},
        options,
      )
    },
    listPaymentLinks(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/payment-links', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createPaymentLink(body, options) {
      return call('POST', '/billing/v1/payment-links', body, options)
    },
    getPaymentLink(id, input, options) {
      return call(
        'GET',
        appendQuery(
          withPathParam('/billing/v1/payment-links/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    updatePaymentLink(id, body, options) {
      return call(
        'PATCH',
        withPathParam('/billing/v1/payment-links/{id}', 'id', id),
        body,
        options,
      )
    },
    deletePaymentLink(id, input, options) {
      return call(
        'DELETE',
        appendQuery(
          withPathParam('/billing/v1/payment-links/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    listRefunds(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/refunds', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createRefund(body, options) {
      return call('POST', '/billing/v1/refunds', body, options)
    },
    getRefund(id, input, options) {
      return call(
        'GET',
        appendQuery(withPathParam('/billing/v1/refunds/{id}', 'id', id), connectionQuery(input)),
        undefined,
        options,
      )
    },
    cancelRefund(id, input, options) {
      return call(
        'POST',
        appendQuery(withPathParam('/billing/v1/refunds/{id}/cancel', 'id', id), connectionQuery(input)),
        {},
        options,
      )
    },
    listSubscriptions(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/subscriptions', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createSubscription(body, options) {
      return call('POST', '/billing/v1/subscriptions', body, options)
    },
    getSubscription(id, input, options) {
      return call(
        'GET',
        appendQuery(
          withPathParam('/billing/v1/subscriptions/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    updateSubscription(id, body, options) {
      return call(
        'PATCH',
        withPathParam('/billing/v1/subscriptions/{id}', 'id', id),
        body,
        options,
      )
    },
    cancelSubscription(id, input, options) {
      return call(
        'POST',
        appendQuery(
          withPathParam('/billing/v1/subscriptions/{id}/cancel', 'id', id),
          connectionQuery(input),
        ),
        {},
        options,
      )
    },
    listInvoices(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/invoices', connectionQuery(input)),
        undefined,
        options,
      )
    },
    getInvoice(id, input, options) {
      return call(
        'GET',
        appendQuery(
          withPathParam('/billing/v1/invoices/{id}', 'id', id),
          connectionQuery(input),
        ),
        undefined,
        options,
      )
    },
    listWebhooks(input, options) {
      return call(
        'GET',
        appendQuery('/billing/v1/webhooks', connectionQuery(input)),
        undefined,
        options,
      )
    },
    createWebhook(input, body, options) {
      return call(
        'POST',
        appendQuery('/billing/v1/webhooks', connectionQuery(input)),
        body,
        options,
      )
    },
    getWebhook(id, input, options) {
      return call(
        'GET',
        appendQuery(withPathParam('/billing/v1/webhooks/{id}', 'id', id), connectionQuery(input)),
        undefined,
        options,
      )
    },
    updateWebhook(id, input, body, options) {
      return call(
        'PATCH',
        appendQuery(withPathParam('/billing/v1/webhooks/{id}', 'id', id), connectionQuery(input)),
        body,
        options,
      )
    },
    deleteWebhook(id, input, options) {
      return call(
        'DELETE',
        appendQuery(withPathParam('/billing/v1/webhooks/{id}', 'id', id), connectionQuery(input)),
        undefined,
        options,
      )
    },
    testWebhook(id, input, options) {
      return call(
        'POST',
        appendQuery(
          withPathParam('/billing/v1/webhooks/{id}/test', 'id', id),
          connectionQuery(input),
        ),
        {},
        options,
      )
    },
    listConnections(clientName, options) {
      return call(
        'GET',
        withPathParam('/admin/billing/clients/{client_name}/connections', 'client_name', clientName),
        undefined,
        options,
      )
    },
    createConnection(clientName, input, options) {
      return call(
        'POST',
        withPathParam('/admin/billing/clients/{client_name}/connections', 'client_name', clientName),
        {
          ownerKind: input.ownerKind,
          ownerId: input.ownerId,
          provider: input.provider,
          mode: input.mode,
          status: input.status,
          credentialKind: input.credentialKind,
          providerAccountId: input.providerAccountId,
          providerProfileId: input.providerProfileId,
          scopes: input.scopes,
          config: input.config,
          metadata: input.metadata,
        },
        options,
      )
    },
    getConnection(clientName, connectionId, options) {
      return call(
        'GET',
        withPathParam(
          withPathParam(
            '/admin/billing/clients/{client_name}/connections/{connection_id}',
            'client_name',
            clientName,
          ),
          'connection_id',
          connectionId,
        ),
        undefined,
        options,
      )
    },
    updateConnection(clientName, connectionId, input, options) {
      return call(
        'PATCH',
        withPathParam(
          withPathParam(
            '/admin/billing/clients/{client_name}/connections/{connection_id}',
            'client_name',
            clientName,
          ),
          'connection_id',
          connectionId,
        ),
        input,
        options,
      )
    },
    deleteConnection(clientName, connectionId, options) {
      return call(
        'DELETE',
        withPathParam(
          withPathParam(
            '/admin/billing/clients/{client_name}/connections/{connection_id}',
            'client_name',
            clientName,
          ),
          'connection_id',
          connectionId,
        ),
        undefined,
        options,
      )
    },
    reconcileDocument(clientName, connectionId, input, options) {
      return call(
        'POST',
        withPathParam(
          withPathParam(
            '/admin/billing/clients/{client_name}/connections/{connection_id}/reconcile',
            'client_name',
            clientName,
          ),
          'connection_id',
          connectionId,
        ),
        {
          resourceKind: input.resourceKind,
          resourceId: input.resourceId,
          customerId: input.customerId,
        },
        options,
      )
    },
    listWebhookEvents(clientName, options) {
      return call(
        'GET',
        withPathParam(
          '/admin/billing/clients/{client_name}/webhook-events',
          'client_name',
          clientName,
        ),
        undefined,
        options,
      )
    },
    provisionWebhookSinks(clientName, input, options) {
      return call(
        'POST',
        appendQuery(
          withPathParam(
            '/admin/billing/clients/{client_name}/webhook-sinks/provision',
            'client_name',
            clientName,
          ),
          {
            targetSchema: input?.targetSchema,
            instance: input?.instance,
          },
        ),
        {},
        options,
      )
    },
    listGrants(options) {
      return call('GET', '/admin/billing/grants', undefined, options)
    },
    listProviders(options) {
      return call('GET', '/admin/billing/providers', undefined, options)
    },
    listSinkHelpers(query, options) {
      return call(
        'GET',
        appendQuery('/admin/webhook-sinks/helpers/billing', {
          targetSchema: query?.targetSchema,
          instance: query?.instance,
        }),
        undefined,
        options,
      )
    },
    ingestProviderWebhook(input, options) {
      const path = withPathParam(
        withPathParam(
          withPathParam(
            '/billing/providers/{provider}/clients/{client_name}/connections/{connection_id}/webhook',
            'provider',
            input.provider,
          ),
          'client_name',
          input.clientName,
        ),
        'connection_id',
        input.connectionId,
      )
      return call('POST', path, input.body, {
        ...options,
        headers: {
          ...(options?.headers ?? {}),
          ...(input.signatureHeaders ?? {}),
        },
      })
    },
    getDebugBilling(jwtSecret, options) {
      return call(
        'GET',
        appendQuery('/debug/billing', { jwt_secret: jwtSecret }),
        undefined,
        options,
        'text',
      )
    },
  }
}
